<template>
    <div class="payment-shell">
        <div class="space-y-5">
            <div>
                <p class="text-xs uppercase tracking-wider text-theme-body-light font-semibold">Payment</p>
                <h3 class="text-lg sm:text-xl font-semibold text-theme-secondary mt-1 leading-tight">
                    You chose {{ chosenMethodLabel }} — send ${{ currencyFormat(totalAmount) }} to release your order
                </h3>
                <p class="text-xs sm:text-sm text-theme-body mt-1">
                    Or pay any of these other ways — we'll match whichever payment we receive to your order.
                </p>
            </div>

            <div class="payment-tabs">
                <button v-for="method in methods" :key="method.key" type="button" class="payment-tab"
                    :class="activePaymentMethod === method.key ? 'is-active' : ''"
                    @click="activePaymentMethod = method.key">
                    <template v-if="method.key === 'zelle'">
                        <span class="payment-tab-z">Z</span>
                    </template>
                    <template v-else-if="method.key === 'wire'">
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M3 10h18" />
                            <path d="M4 10V8l8-4 8 4v2" />
                            <path d="M6 10v8" />
                            <path d="M10 10v8" />
                            <path d="M14 10v8" />
                            <path d="M18 10v8" />
                            <path d="M2 18h20" />
                        </svg>
                    </template>
                    <template v-else>
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <rect x="3" y="6" width="18" height="12" rx="2" />
                            <path d="M7 10h6" />
                            <path d="M7 14h3" />
                        </svg>
                    </template>

                    <span class="hidden sm:inline">{{ method.label }}</span>
                    <span class="sm:hidden">{{ method.shortLabel }}</span>

                    <span v-if="activePaymentMethod === method.key" class="hidden sm:inline-flex payment-tab-badge">
                        You chose
                    </span>
                    <span v-if="activePaymentMethod === method.key" class="sm:hidden payment-tab-dot"></span>

                    <span v-if="activePaymentMethod === method.key" class="payment-tab-underline"></span>
                </button>
            </div>

            <ZelleInstructions v-if="activePaymentMethod === 'zelle'" :zelle-email="zelleEmail"
                :invoice-email="invoiceEmail" :zelle-reference="zelleReference" :total-amount="totalAmount"
                :selected-invoices-count="selectedInvoicesCount" :copied-key="copiedKey"
                :copy-to-clipboard="copyToClipboard" :currency-format="currencyFormat" layout="full" />

            <WireInstructions v-else-if="activePaymentMethod === 'wire'" :wire-details="wireDetails"
                :invoice-email="invoiceEmail" :zelle-reference="zelleReference" :total-amount="totalAmount"
                :selected-invoices-count="selectedInvoicesCount" :copied-key="copiedKey"
                :copy-to-clipboard="copyToClipboard" :currency-format="currencyFormat" layout="full" />

            <!-- <CheckInstructions v-else :check-pay-to="checkPayTo" :check-mail-to="checkMailTo"
                :invoice-email="invoiceEmail" :zelle-reference="zelleReference" :total-amount="totalAmount"
                :copied-key="copiedKey" :copy-to-clipboard="copyToClipboard" :currency-format="currencyFormat"
                layout="full" /> -->
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import ZelleInstructions from "@/components/payment-instructions/ZelleInstructions.vue";
import WireInstructions from "@/components/payment-instructions/WireInstructions.vue";
// import CheckInstructions from "@/components/payment-instructions/CheckInstructions.vue";

const props = defineProps({
    selectedMethod: { type: String, default: "wire" },
    zelleEmail: { type: String, default: "" },
    zelleReference: { type: String, default: "" },
    totalAmount: { type: [String, Number], default: "0" },
    selectedInvoicesCount: { type: Number, default: 0 },
    copiedKey: { type: String, default: "" },
    copyToClipboard: { type: Function, required: true },
    currencyFormat: { type: Function, required: true },
    wireDetails: { type: Object, required: true },
    checkPayTo: { type: String, default: "" },
    checkMailTo: { type: String, default: "" },
    invoiceEmail: { type: String, default: "" },
});

const methods = [
    { key: "zelle", label: "Zelle", shortLabel: "Zelle" },
    { key: "wire", label: "Wire / ACH", shortLabel: "Wire" },
    // { key: "check", label: "Check", shortLabel: "Check" },
];

const normalizedMethod = (method) => {
    if (method === "zelle" || method === "wire" || method === "check") {
        return method;
    }
    return "wire";
};

const activePaymentMethod = ref(normalizedMethod(props.selectedMethod));

const chosenMethodLabel = computed(() => {
    return methods.find((method) => method.key === normalizedMethod(props.selectedMethod))?.label || "Wire / ACH";
});

watch(
    () => props.selectedMethod,
    (nextMethod) => {
        activePaymentMethod.value = normalizedMethod(nextMethod);
    },
);
</script>

<style scoped>
.payment-shell {
    border: 1px solid rgba(230, 232, 238, 0.7);
    border-radius: 14px;
    background: #fff;
    box-shadow: none;
    padding: 22px 26px;
}

.payment-tabs {
    display: flex;
    align-items: stretch;
    justify-content: space-between;
    gap: 2px;
    border-bottom: 1px solid rgba(230, 232, 238, 0.55);
}

.payment-tab {
    position: relative;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 10px;
    font-size: 13px;
    font-weight: 600;
    color: #596275;
    transition: color 140ms ease;
}

.payment-tab:hover {
    color: #3f4758;
}

.payment-tab.is-active {
    color: #2b303f;
}

.payment-tab-z {
    width: 20px;
    height: 20px;
    border-radius: 6px;
    border: 1px solid transparent;
    background: #7c3aed;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.payment-tab-badge {
    align-items: center;
    border-radius: 999px;
    padding: 1px 6px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    background: #f6eee6;
    color: #bd966f;
}

.payment-tab-dot {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: #bd966f;
    flex-shrink: 0;
}

.payment-tab-underline {
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 2px;
    border-radius: 999px 999px 0 0;
    background: #bd966f;
}
</style>
